import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Card,
  CardContent,
  Grid,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  CardActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PricingTable from "./PricingTable";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";

const PricingFrame = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(1); // 0: 2025-2026, 1: 2026-2027 (default to new year)

  const pricingPackages = ["english", "russian"];
  const academicYears = ["2025-2026", "2026-2027"];
  const academicYearKeys = ["year_2025_2026", "year_2026_2027"];
  const currentPackage = pricingPackages[activeTab];
  const currentYear = academicYears[selectedYear];
  const currentYearKey = academicYearKeys[selectedYear];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleYearChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedYear(newValue);
  };

  const handleRedirection = () => {
    const path =
      currentLanguage === "en"
        ? "/our-program"
        : `/${currentLanguage}/our-program`;
    history.push(path);
  };

  return (
    <Box px={isMobile ? "5%" : "15%"} pt={10} pb={4}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        {pricingPackages.map((pkgKey) => (
          <Tab
            key={pkgKey}
            label={t(`PricingPage.PricingFrame.${pkgKey}.tabLabel`)}
            sx={{
              ...theme.typography.h4,
              fontWeight: 600,
            }}
          />
        ))}
      </Tabs>

      {/* Academic Year Selector */}
      <Tabs
        value={selectedYear}
        onChange={handleYearChange}
        textColor="primary"
        indicatorColor="primary"
        variant="fullWidth"
        sx={{ mb: 4 }}
      >
        {academicYears.map((year) => (
          <Tab
            key={year}
            label={t(`PricingPage.PricingFrame.academicYear.${year}`)}
            sx={{
              ...theme.typography.h6,
              fontWeight: 500,
            }}
          />
        ))}
      </Tabs>

      {/* Cards */}
      <Grid container spacing={2} mb={8}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              borderRadius: "18px",
              textAlign: "center",
              border: `3px solid ${theme.palette.primary.light}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <CardHeader
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  border: `3px solid ${theme.palette.primary.light}`,
                  minHeight: "110px",
                }}
                title={
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t(`PricingPage.PricingFrame.${currentPackage}.title`)}
                  </Typography>
                }
                subheader={
                  <Typography variant="subtitle1" gutterBottom>
                    {t(`PricingPage.PricingFrame.${currentPackage}.subtitle`)}
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  borderBottomRightRadius: "18px",
                  borderBottomLeftRadius: "18px",
                }}
              >
                <List sx={{ paddingLeft: 0 }} dense>
                  {(
                    t(`PricingPage.PricingFrame.${currentPackage}.included`, {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <ListItem
                      key={index}
                      disableGutters
                      sx={{ justifyContent: "center", textAlign: "center" }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" textAlign="center">
                            {item}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              borderRadius: "18px",
              textAlign: "center",
              border: `3px solid ${theme.palette.primary.light}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <CardHeader
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  border: `3px solid ${theme.palette.primary.light}`,
                  minHeight: "110px",
                }}
                title={
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t("PricingPage.PricingFrame.optionalTitle")}
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  borderBottomRightRadius: "18px",
                  borderBottomLeftRadius: "18px",
                }}
              >
                <List sx={{ paddingLeft: 0 }} dense>
                  {(
                    t(`PricingPage.PricingFrame.${currentPackage}.optional`, {
                      returnObjects: true,
                    }) as string[]
                  ).map((item, index) => (
                    <ListItem
                      key={index}
                      disableGutters
                      sx={{ justifyContent: "center", textAlign: "center" }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" textAlign="center">
                            {item}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </div>
            <CardActions
              sx={{
                display: "flex",
                justifyContent: "center",
                pb: "16px",
              }}
            >
              <OrangeHighlightButton onClick={handleRedirection} sx={{ mt: 4 }}>
                {t(`PricingPage.PricingFrame.${currentPackage}.ctas.more`)}
              </OrangeHighlightButton>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <PricingTable
        currentPackage={currentPackage}
        activeTab={activeTab}
        academicYear={currentYear}
        academicYearKey={currentYearKey}
      />
    </Box>
  );
};

export default PricingFrame;
